import { Facility, LocationSearchParams } from '@gyym/sdk'
import { FacilityCard } from '@gyym/ui'
import { GpsFixed, List, SearchCondition } from '@gyym/ui/icons'
import { Circle } from '@react-google-maps/api'
import React from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import useDraggableScroll from 'use-draggable-scroll'

import Map from '@/components/UI/Map'
import useGeolocation from '@/hooks/useGeolocation'
import { useMapFacilities } from '@/modules/Facilities'
import { useToggleFavorite } from '@/modules/Favorites'

import {
  ButtonGroup,
  ButtonItem,
  FacilityCardContainer,
  FacilityCardInner,
  GpsButton,
  IconWrapper,
  MapContainer,
} from './styles'
import { Location } from './types'
import { getLocationsWithin } from './utils'

// Initial zoom
const INITIAL_ZOOM = 12

// Marker grouping distance in km
const GROUPING_DISTANCE_IN_KM = 0.001

export interface MapViewProps {
  center?: { lat: number; lng: number }
  skipInitialGeolocationPrompt?: boolean
  iframeForceViewMode?: 'pc' | 'sp' | 'simple-embed'
  searchParams?: Omit<
    LocationSearchParams,
    'latitude' | 'longitude' | 'radius' | 'omitPrograms'
  >
  onFacilitiesChange?: (
    facilities: (Facility & { lat: number; lng: number })[],
  ) => void
  onListViewClick?: () => void
  onConditionClick?: () => void
}

const zoomToRadius = (zoom: number) => {
  let radius = 300000

  if (zoom > 8) {
    radius = radius / Math.pow(2, zoom - 8)
  }

  return radius / 1.5
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useDebounce = (value: any, delay = 1000) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

const useMapCenter = (
  center: { lat: number; lng: number } | undefined,
  currentLocation: { lat: number; lng: number },
) => {
  // Current map center
  const [mapCenter, setMapCenter] = React.useState<{
    lat: number
    lng: number
  }>(center ?? currentLocation)

  // Set current location only once to avoid map jumping
  React.useEffect(() => {
    setMapCenter(center ?? currentLocation)
  }, [center, currentLocation])

  // Debounce value to omit unnecessary requests
  const debouncedMapCenter = useDebounce(mapCenter, 1000)

  return { mapCenter, debouncedMapCenter, setMapCenter }
}

export const MapView = ({
  center,
  skipInitialGeolocationPrompt = false,
  iframeForceViewMode,
  searchParams: params,
  onFacilitiesChange,
  onListViewClick,
  onConditionClick,
}: MapViewProps) => {
  const cardContainerRef = React.useRef<HTMLDivElement>(null)

  // Facility ids selected from the cluster/pin
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])

  // Map instance
  const [map, setMap] = React.useState<google.maps.Map>()

  const { currentLocation, getLocation, permissionState } = useGeolocation({
    enableHighAccuracy: true,
    skipInitialPrompt: skipInitialGeolocationPrompt,
  })

  // Handle map center
  const { mapCenter, debouncedMapCenter, setMapCenter } = useMapCenter(
    center,
    currentLocation,
  )

  // Current zoom level
  const [zoom, setZoom] = React.useState(INITIAL_ZOOM)
  const radius = zoomToRadius(zoom)
  const debouncedZoom = useDebounce(zoom)

  // Facility search params
  const [searchParams, setSearchParams] = React.useState<
    Partial<LocationSearchParams>
  >({
    ...params,
    numPerPage: 999,
    omitPrograms: true,
    // Fetch all facilities (don't restrict to current location) when areaIds specified so we can pan to the first result
    ...(!params?.areaIds?.length
      ? {
          latitude: mapCenter.lat,
          longitude: mapCenter.lng,
          // calculate radius from the current zoom level
          radius,
        }
      : {
          latitude: undefined,
          longitude: undefined,
          radius: undefined,
        }),
  })

  // Cached facilities by id
  // Note that the cache gets cleared when searchParams changes
  const shouldFetch = permissionState !== null
  const {
    data: facilitiesById,
    conditionChanged,
    isValidating,
  } = useMapFacilities(
    searchParams,
    // Only fetch when the geolocation permission state is loaded
    shouldFetch,
  )

  // Map load
  const handleLoad = React.useCallback((map: google.maps.Map) => {
    setMap(map)
  }, [])

  // Pan the map to current location
  const handleGoToCurrentLocation = React.useCallback(async () => {
    if (!map) return

    // Load geolocation if not loaded
    const location = await getLocation()

    map.panTo(location)
    setMapCenter(location)
  }, [getLocation, map, setMapCenter])

  // Update search params when map center / zoom changes
  React.useEffect(() => {
    setSearchParams((p) => ({
      ...p,
      ...params,
      numPerPage: 999,
      omitPrograms: true,
      // Fetch all facilities (don't restrict to current location) when areaIds specified so we can pan to the first result
      ...(!params?.areaIds?.length
        ? {
            latitude: debouncedMapCenter.lat,
            longitude: debouncedMapCenter.lng,
            // calculate radius from the current zoom level
            radius: zoomToRadius(debouncedZoom),
          }
        : {
            latitude: undefined,
            longitude: undefined,
            radius: undefined,
          }),
    }))
  }, [debouncedMapCenter, debouncedZoom, params])

  // Pan the map to the first facility if areaIds is specified
  React.useEffect(() => {
    // Skip if map not loaded yet
    if (!map) return

    // Skip if areaIds is empty
    if (!params?.areaIds?.length) return
    // Skip if data is still validating
    if (isValidating) return

    // pan to first facility if exists
    const facilities = Object.values(facilitiesById)
    if (!facilities[0]) return
    map.panTo({ lat: facilities[0].lat, lng: facilities[0].lng })
  }, [params?.areaIds, facilitiesById, map, conditionChanged, isValidating])

  // Deselect facilities when condition changes
  React.useEffect(() => {
    if (!conditionChanged) return

    // Deselect all facilities
    setSelectedIds([])
  }, [conditionChanged])

  const { onMouseDown } = useDraggableScroll(
    cardContainerRef as React.RefObject<HTMLDivElement>,
    {
      direction: 'horizontal',
    },
  )

  // Wether to show the facility favorite button
  const isFacilityFavoriteEnabled = iframeForceViewMode !== 'simple-embed'

  const { isFavoriteLoading, favoritedFacilityIds, onToggle } =
    useToggleFavorite({
      target: '_parent',
      enabled: isFacilityFavoriteEnabled,
    })

  const onMarkerClick = React.useCallback(
    (location: Location) => {
      const locations = Object.values(facilitiesById)
      const targetLocations = getLocationsWithin(
        location,
        GROUPING_DISTANCE_IN_KM,
        locations,
      )
      const targetIds = targetLocations.map((l) => l.id)

      // Check value equality
      const isEqual =
        selectedIds.length === targetIds.length &&
        selectedIds.sort().toString() === targetIds.sort().toString()

      if (!isEqual) {
        setSelectedIds(targetIds)
      } else {
        // Deselect values to toggle if equal
        setSelectedIds([])
      }
    },
    [selectedIds, facilitiesById],
  )

  const handleZoom = React.useCallback(
    (zoom: number) => {
      setZoom(zoom)
      if (!map) return
      const center = map.getCenter()
      if (!center) return
      setMapCenter({ lat: center.lat(), lng: center.lng() })
    },
    [map, setMapCenter],
  )

  const handleDragEnd = React.useCallback(
    (center: google.maps.LatLng) =>
      setMapCenter({ lat: center.lat(), lng: center.lng() }),
    [setMapCenter],
  )

  React.useEffect(() => {
    onFacilitiesChange?.(Object.values(facilitiesById))
  }, [onFacilitiesChange, facilitiesById])

  // Debugger
  const [showDebugCircle, setShowDebugCircle] = React.useState(false)
  useHotkeys('shift+d', () => setShowDebugCircle((v) => !v))

  return (
    <MapContainer>
      <Map
        zoom={INITIAL_ZOOM}
        iframeMode={!!iframeForceViewMode}
        center={mapCenter}
        currentLocation={currentLocation}
        locations={Object.values(facilitiesById)}
        selectedIds={selectedIds}
        onLoad={handleLoad}
        onMarkerClick={onMarkerClick}
        onZoomChanged={handleZoom}
        onDragEnd={handleDragEnd}
      >
        {showDebugCircle && (
          <Circle
            center={mapCenter}
            radius={radius}
            options={{
              strokeColor: '#FF0000',
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: '#FF0000',
              fillOpacity: 0.1,
            }}
          />
        )}
      </Map>

      <GpsButton onClick={handleGoToCurrentLocation}>
        <GpsFixed />
      </GpsButton>

      {selectedIds.length > 0 && (
        <FacilityCardContainer
          ref={cardContainerRef}
          onMouseDown={onMouseDown}
          iframeForceViewMode={iframeForceViewMode}
        >
          <FacilityCardInner>
            {selectedIds.map((id) => (
              <FacilityCard
                key={id}
                variant="map"
                id={id}
                name={facilitiesById[id]?.name ?? ''}
                href={`/facilities/${id}`}
                station={facilitiesById[id]?.station ?? ''}
                images={facilitiesById[id]?.images ?? []}
                reviewCount={facilitiesById[id]?.reviewCount ?? 0}
                reviewScore={facilitiesById[id]?.reviewScore ?? 0}
                activities={facilitiesById[id]?.activities ?? []}
                isFavorited={favoritedFacilityIds.includes(id)}
                isFavoriteLoading={isFavoriteLoading}
                onFavoriteClick={
                  isFacilityFavoriteEnabled ? onToggle : undefined
                }
              />
            ))}
          </FacilityCardInner>
        </FacilityCardContainer>
      )}

      {/* Show only on iframe 'sp' mode */}
      {iframeForceViewMode === 'sp' && (
        <ButtonGroup>
          <ButtonItem role="button" onClick={onConditionClick}>
            {/* TODO: Show indicator when condition has changes */}
            <IconWrapper indicator={false}>
              <SearchCondition />
            </IconWrapper>
            条件を変更
          </ButtonItem>

          <ButtonItem role="button" onClick={onListViewClick}>
            <IconWrapper>
              <List />
            </IconWrapper>
            リスト表示
          </ButtonItem>
        </ButtonGroup>
      )}
    </MapContainer>
  )
}
